/**
 * DocsSidebar
 * 
 * Navigation tree for documentation.
 * Shows categories, subcategories, and pages.
 * 
 * Design: Text-first, no icons. Relies on typography and spacing
 * for hierarchy. Disclosure arrows for expand/collapse only.
 */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useDocsStore } from '../../store/docsStore';
import type { DocNavNode } from '../../docs/types';

interface NavItemProps {
    node: DocNavNode;
    depth: number;
}

const NavItem: React.FC<NavItemProps> = ({ node, depth }) => {
    const {
        activeDocId,
        expandedCategories,
        toggleCategory,
        navigateTo
    } = useDocsStore();

    const isExpanded = !!expandedCategories[node.id];
    const isActive = node.docId === activeDocId;
    const hasChildren = node.children && node.children.length > 0;
    const isCategory = node.type === 'category';
    const isSection = node.type === 'section';

    // Section headers are always expanded (non-collapsible labels)
    if (isSection) {
        return (
            <div className={depth === 0 ? 'mt-4 first:mt-0' : ''}>
                <div className="px-3 pt-3 pb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        {node.title}
                    </span>
                </div>
                {hasChildren && (
                    <div>
                        {node.children!.map((child) => (
                            <NavItem key={child.id} node={child} depth={depth} />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    const handleClick = () => {
        if (node.type === 'page' && node.docId) {
            navigateTo(node.docId);
        } else if (hasChildren) {
            toggleCategory(node.id);
        }
    };

    return (
        <div>
            <button
                onClick={handleClick}
                className={`
                    w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm
                    transition-colors rounded-md
                    ${isActive
                        ? 'bg-cyan-600/20 text-cyan-600 dark:text-cyan-400'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5'
                    }
                `}
                style={{ paddingLeft: `${12 + depth * 12}px` }}
            >
                {/* Disclosure arrow for expandable items */}
                {hasChildren ? (
                    <FontAwesomeIcon
                        icon={isExpanded ? faChevronDown : faChevronRight}
                        className="h-2.5 w-2.5 text-gray-500 flex-shrink-0"
                    />
                ) : (
                    // Spacer to align leaf items with parent text
                    <span className="w-2.5" />
                )}

                {/* Title - heavier weight for categories */}
                <span className={isCategory ? 'font-medium text-gray-700 dark:text-gray-300' : ''}>
                    {node.title}
                </span>
            </button>

            {/* Children */}
            {hasChildren && isExpanded && (
                <div>
                    {node.children!.map((child) => (
                        <NavItem key={child.id} node={child} depth={depth + 1} />
                    ))}
                </div>
            )}
        </div>
    );
};

export const DocsSidebar: React.FC = () => {
    const { navTree } = useDocsStore();

    return (
        <nav className="w-56 border-r border-gray-200 dark:border-white/[0.06] overflow-y-auto py-2 flex-shrink-0">
            {navTree.map((node) => (
                <NavItem key={node.id} node={node} depth={0} />
            ))}
        </nav>
    );
};

export default DocsSidebar;
